import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import AdminSidebar from "./AdminSidebar";
import AdminTopbar from "./Admintopbar";

import {
  getVerifications,
  updateVerification,
} from "../api/adminclient";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export default function AdminVerification() {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadVerifications = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getVerifications();

      const data = Array.isArray(response.data)
        ? response.data
        : [];

      setVerifications(data);
    } catch (err) {
      console.error(
        "Failed to load verification records:",
        err
      );

      setError(
        err?.response?.data?.detail ||
        "Could not load verification records."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVerifications();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [verifications.length, pageSize]);

  const setStatus = async (verification, status) => {
    setError("");

    try {
      const response = await updateVerification(
        verification.id,
        { status }
      );

      const updated = response.data;

      setVerifications((current) =>
        current.map((item) =>
          item.id === verification.id
            ? {
                ...item,
                status: updated.status,
                verifiedAt: updated.verifiedAt,
                expiresAt: updated.expiresAt,
              }
            : item
        )
      );
    } catch (err) {
      console.error(
        "Failed to update verification:",
        err
      );

      setError(
        err?.response?.data?.detail ||
        "Could not update verification status."
      );
    }
  };

  const tone = (status) => {
    if (status === "Verified") {
      return "active";
    }

    if (status === "Pending") {
      return "pending";
    }

    return "inactive";
  };

  const totalPages = Math.max(
    1,
    Math.ceil(verifications.length / pageSize)
  );

  const safePage = Math.min(
    page,
    totalPages
  );

  const pagedVerifications = useMemo(() => {
    const start =
      (safePage - 1) * pageSize;

    return verifications.slice(
      start,
      start + pageSize
    );
  }, [
    verifications,
    safePage,
    pageSize,
  ]);

  const rangeStart =
    verifications.length === 0
      ? 0
      : (safePage - 1) * pageSize + 1;

  const rangeEnd = Math.min(
    safePage * pageSize,
    verifications.length
  );

  const goToPage = (targetPage) => {
    setPage(
      Math.min(
        Math.max(1, targetPage),
        totalPages
      )
    );
  };

  const pageNumbers = useMemo(() => {
    const numbers = [];

    const add = (number) => {
      if (
        number >= 1 &&
        number <= totalPages &&
        !numbers.includes(number)
      ) {
        numbers.push(number);
      }
    };

    add(1);
    add(safePage - 1);
    add(safePage);
    add(safePage + 1);
    add(totalPages);

    return numbers.sort(
      (a, b) => a - b
    );
  }, [safePage, totalPages]);

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />

      <div className="admin-content">
        <AdminTopbar />

        <h2 className="h5 fw-bold mb-3">
          Verification Codes
        </h2>

        {error && (
          <div className="alert alert-warning py-2 small">
            {error}
          </div>
        )}

        <div className="admin-card p-3 p-md-4">

          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">

            <h3 className="h6 fw-bold mb-0 d-none d-md-block">
              Records
            </h3>

            <div className="d-flex align-items-center gap-2 ms-auto">

              <label className="small text-muted mb-0">
                Rows per page
              </label>

              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={pageSize}
                onChange={(event) =>
                  setPageSize(
                    Number(event.target.value)
                  )
                }
              >
                {PAGE_SIZE_OPTIONS.map(
                  (number) => (
                    <option
                      key={number}
                      value={number}
                    >
                      {number}
                    </option>
                  )
                )}
              </select>

            </div>
          </div>

          {loading && (
            <p className="text-muted mb-0">
              Loading verification records...
            </p>
          )}

          {!loading &&
            verifications.length === 0 && (
              <p className="text-muted small mb-0">
                No verification records.
              </p>
            )}

          {!loading &&
            verifications.length > 0 && (
              <>
                <div className="table-responsive">

                  <table className="table align-middle admin-table mb-0">

                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Student</th>
                        <th>Exam</th>
                        <th>Scheduled</th>
                        <th>Status</th>
                        <th className="text-end">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {pagedVerifications.map(
                        (verification, index) => (
                          <tr
                            key={
                              verification.id
                            }
                          >
                            <td className="text-muted">
                              {(safePage - 1) *
                                pageSize +
                                index +
                                1}
                            </td>

                            <td className="fw-semibold">
                              {verification.studentName ||
                                "—"}
                            </td>

                            <td>
                              {verification.examName ||
                                "—"}
                            </td>

                            <td className="text-muted small">
                              {verification.scheduled ||
                                "—"}
                            </td>

                            <td>
                              <span
                                className={`admin-status-pill ${tone(
                                  verification.status
                                )}`}
                              >
                                {verification.status}
                              </span>
                            </td>

                            <td className="text-end">

                              {verification.status !==
                                "Verified" && (
                                <button
                                  className="btn btn-sm btn-outline-success me-1"
                                  onClick={() =>
                                    setStatus(
                                      verification,
                                      "Verified"
                                    )
                                  }
                                >
                                  Verify
                                </button>
                              )}

                              {verification.status !==
                                "Expired" && (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() =>
                                    setStatus(
                                      verification,
                                      "Expired"
                                    )
                                  }
                                >
                                  Expire
                                </button>
                              )}

                            </td>
                          </tr>
                        )
                      )}
                    </tbody>

                  </table>

                </div>

                <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">

                  <div className="small text-muted">
                    Showing {rangeStart}–
                    {rangeEnd} of{" "}
                    {verifications.length}
                  </div>

                  <nav aria-label="Verification pagination">

                    <ul className="pagination pagination-sm mb-0">

                      <li
                        className={`page-item ${
                          safePage === 1
                            ? "disabled"
                            : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            goToPage(
                              safePage - 1
                            )
                          }
                        >
                          Prev
                        </button>
                      </li>

                      {pageNumbers.map(
                        (number, index) => {
                          const previous =
                            pageNumbers[
                              index - 1
                            ];

                          const showEllipsis =
                            previous !==
                              undefined &&
                            number -
                              previous >
                              1;

                          return (
                            <React.Fragment
                              key={number}
                            >

                              {showEllipsis && (
                                <li className="page-item disabled">
                                  <span className="page-link">
                                    …
                                  </span>
                                </li>
                              )}

                              <li
                                className={`page-item ${
                                  number ===
                                  safePage
                                    ? "active"
                                    : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() =>
                                    goToPage(
                                      number
                                    )
                                  }
                                >
                                  {number}
                                </button>
                              </li>

                            </React.Fragment>
                          );
                        }
                      )}

                      <li
                        className={`page-item ${
                          safePage ===
                          totalPages
                            ? "disabled"
                            : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            goToPage(
                              safePage + 1
                            )
                          }
                        >
                          Next
                        </button>
                      </li>

                    </ul>

                  </nav>

                </div>
              </>
            )}

        </div>
      </div>
    </div>
  );
}